import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser, setSentryCompany, Sentry } from "@/lib/sentry";
import { identifyUser, resetPostHog } from "@/lib/posthog";
import { queryClient } from "@/lib/query-client";
import { getBrowserFingerprint, getOrCreateDeviceId } from "@/lib/session-fingerprint";
import { clearBatchInflight } from "@/components/partner/scanner-v2/batchInflight";
import { clearInflightScan } from "@/components/partner/scanner-v2/scanInflight";
import { isLockStealError } from "@/lib/lock-steal";
import type { User } from "@supabase/supabase-js";

interface AalData {
  currentLevel: string;
  nextLevel: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  companyId: string | null;
  aal: AalData | null;
  connectionError: boolean;
  /** The auth bootstrap lost the origin-scoped Web Lock to ANOTHER TAB
   *  (cross-tab steal, the gap the serialized checkSession can't fix).
   *  ProtectedRoute renders the "open in another tab" screen for this instead
   *  of the generic Connection Issue screen, which read as a server outage
   *  (cowork audit 07-23 §6.5). */
  multiTabConflict: boolean;
  signOut: () => Promise<void>;
  retryConnection: () => void;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true, companyId: null, aal: null, connectionError: false, multiTabConflict: false, signOut: async () => {}, retryConnection: () => {} });

// A rejected access token (expired, or signed by a rotated/revoked key) is
// recoverable via a refresh-token exchange. A token whose *subject* is missing
// or malformed is not — that one is genuinely the wrong shape and must be
// wiped. We treat the common bad_jwt / expired / invalid signature cases as
// recoverable and let refreshSession() decide; only a successful refresh keeps
// the user signed in.
function isRecoverableJwtError(message: string | undefined): boolean {
  const msg = (message || '').toLowerCase();
  return msg.includes('jwt') || msg.includes('sub claim') || msg.includes('invalid') || msg.includes('expired');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [aal, setAal] = useState<AalData | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [multiTabConflict, setMultiTabConflict] = useState(false);
  // One silent re-check per bootstrap attempt when the Web Lock was stolen by
  // another tab (steals are transient - the thief holds the lock for one
  // refresh). Reset by retryConnection so every manual Try Again earns one
  // fresh silent retry. Bounded on purpose: two tabs both auto-retrying
  // forever would ping-pong the lock.
  const lockStealRetriedRef = useRef(false);
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);
  // Tracks the currently-authenticated user id across auth events. The
  // onAuthStateChange closure captures a stale `user`, so we read the prior
  // identity from this ref to tell a genuine sign-in (no prior user) apart from
  // a SIGNED_IN that Supabase re-emits on tab-refocus / token-refresh while a
  // session already exists. Only the former should gate the whole app behind
  // the loading spinner (which unmounts the dashboard via ProtectedRoute).
  const userIdRef = useRef<string | null>(null);
  // Holds the single in-flight session check so the app never runs two
  // getSession() calls at once (see checkSession below).
  const sessionCheckRef = useRef<Promise<void> | null>(null);

  const fetchCompany = async (
    userId: string,
  ): Promise<{ id: string | null; isTestTenant: boolean | undefined }> => {
    // Hit owner + role membership in parallel. Owner wins if both return; the
    // sequential 3s-race version was firing the timeout on a healthy query
    // and cascading into a slower fallback path, leaving /dash stuck on a
    // loading shell for 6+ seconds.
    //
    // is_test_tenant rides along for analytics identity (PostHog test-account
    // filtering). The owner path gets it free from the same row; the member
    // path pays one follow-up select on the resolved company (members are the
    // rare case). `undefined` = unknown, and identifyUser omits it then.
    let resolved: string | null = null;
    let isTestTenant: boolean | undefined = undefined;
    try {
      const [ownerRes, memberRes] = await Promise.allSettled([
        supabase.from('companies').select('id, is_test_tenant').eq('owner_user_id', userId).limit(1).maybeSingle(),
        supabase.from('company_roles').select('company_id').eq('user_id', userId).not('accepted_at', 'is', null).limit(1).maybeSingle(),
      ]);
      const ownerRow = ownerRes.status === 'fulfilled' ? ownerRes.value.data ?? null : null;
      const memberId = memberRes.status === 'fulfilled' ? memberRes.value.data?.company_id ?? null : null;
      resolved = ownerRow?.id ?? memberId ?? null;
      if (ownerRow?.id) {
        isTestTenant = ownerRow.is_test_tenant === true;
      } else if (resolved) {
        // Member path: the flag lives on the companies row, not the role row.
        // RLS permits reading your own company (usePlanUsage does the same).
        const { data } = await supabase
          .from('companies').select('is_test_tenant').eq('id', resolved).maybeSingle();
        if (data) isTestTenant = data.is_test_tenant === true;
      }
    } catch (e: unknown) {
      console.error('fetchCompany error:', e);
      resolved = null;
    }
    if (mountedRef.current) setCompanyId(resolved);
    return { id: resolved, isTestTenant };
  };

  // Highest-privilege role for analytics cohorts (admin > moderator > user).
  // Uses the has_role RPC so RLS on user_roles stays untouched.
  const fetchHighestRole = async (): Promise<"admin" | "moderator" | "user"> => {
    try {
      const [adminRes, modRes] = await Promise.allSettled([
        supabase.rpc('current_user_has_role', { _role: 'admin' }),
        supabase.rpc('current_user_has_role', { _role: 'moderator' }),
      ]);
      if (adminRes.status === 'fulfilled' && adminRes.value.data === true) return 'admin';
      if (modRes.status === 'fulfilled' && modRes.value.data === true) return 'moderator';
      return 'user';
    } catch {
      return 'user';
    }
  };

  const fetchAal = async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (mountedRef.current) setAal(data);
    } catch (e) {
      console.error('fetchAal error:', e);
      if (mountedRef.current) setAal(null);
    }
  };

  const handleSession = async (sessionUser: User | null) => {
    if (!mountedRef.current) return;
    setUser(sessionUser);
    // Keep the prior-identity ref current (the initial getSession() path calls
    // handleSession directly, not through the listener), so the listener's
    // "is this a genuine new sign-in?" check is correct after a page load.
    userIdRef.current = sessionUser?.id ?? null;
    // Sync Sentry user context
    setSentryUser(sessionUser ? { id: sessionUser.id, email: sessionUser.email } : null);
    if (sessionUser) {
      // Eager identify so events captured before the company/role lookups
      // resolve are still attributed to this user.
      identifyUser({ id: sessionUser.id });
      const [company, , role] = await Promise.all([
        fetchCompany(sessionUser.id),
        fetchAal(),
        fetchHighestRole(),
      ]);
      const resolvedCompanyId = company.id;
      // Re-identify with enriched cohort properties for tenant-level analytics.
      // is_test_tenant lets PostHog's test-account filter exclude QA tenants.
      identifyUser({
        id: sessionUser.id,
        companyId: resolvedCompanyId ?? undefined,
        role,
        isTestTenant: company.isTestTenant,
      });
      // Tag Sentry with the tenant now that the company is resolved (the
      // setSentryUser call above fires pre-resolve, so it can't carry this).
      setSentryCompany(resolvedCompanyId ?? null);
      // Fire-and-forget: populate session_fingerprints for sybil-defense layer.
      // Never blocks auth flow; server deduplicates repeat calls.
      void (async () => {
        try {
          const fp = await getBrowserFingerprint();
          const did = getOrCreateDeviceId();
          await supabase.functions.invoke("session-capture", {
            body: { browser_fingerprint: fp, device_id: did },
          });
        } catch (e) {
          Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
            level: "warning",
            tags: { source: "session-capture" },
          });
        }
      })();
      if (mountedRef.current) setLoading(false);
    } else {
      resetPostHog();
      setCompanyId(null);
      setAal(null);
      setLoading(false);
    }
  };

  // Serialized session check. gotrue's auth-token Web Lock is origin-scoped, so
  // two concurrent getSession() calls steal the lock from each other in a
  // non-converging loop (console: "Lock broken by another request with the
  // 'steal' option" → "Retry getSession failed"). That happened because the 8s
  // timeout below shows the Connection-Issue screen while the ORIGINAL
  // getSession() is still waiting on the lock, and clicking Try Again fired a
  // SECOND getSession() that fought the first. A StrictMode double-mount does
  // the same. Reusing one in-flight promise guarantees this tab only ever runs
  // one getSession() at a time. (It does NOT fix cross-tab contention — see the
  // PR note about switching the client to `lock: processLock`.)
  const checkSession = (): Promise<void> => {
    if (sessionCheckRef.current) return sessionCheckRef.current;

    const run = (async () => {
      const sessionTimeout = setTimeout(() => {
        if (!initializedRef.current && mountedRef.current) {
          console.warn('Supabase session check timed out after 8s');
          // An 8s stall while ANOTHER context holds the sb auth-token Web Lock
          // is cross-tab contention, not a server outage - route it to the
          // multi-tab screen instead of Connection Issue. Heuristic: our own
          // serialized check never both holds and waits on the lock, so a held
          // auth-token lock at timeout means another tab. locks.query is
          // Chrome-era API - any failure falls through to the generic screen.
          const finish = (multiTab: boolean) => {
            if (initializedRef.current || !mountedRef.current) return;
            if (multiTab) setMultiTabConflict(true);
            else setConnectionError(true);
            setLoading(false);
          };
          try {
            navigator.locks
              .query()
              .then((state) =>
                finish(
                  (state.held ?? []).some((l) => (l.name ?? "").includes("auth-token")),
                ),
              )
              .catch(() => finish(false));
          } catch {
            finish(false);
          }
        }
      }, 8000);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        clearTimeout(sessionTimeout);
        initializedRef.current = true;
        lockStealRetriedRef.current = false;
        if (mountedRef.current) {
          setConnectionError(false);
          setMultiTabConflict(false);
        }

        // Defensive: if we have a session token but the server rejects it
        // ("bad_jwt" — the ACCESS token expired, or was signed by a Supabase
        // signing key that has since been rotated/revoked), do NOT sign the
        // user out on sight. The REFRESH token is almost always still valid, so
        // mint a fresh access token first and only force a clean re-login if
        // that refresh also fails (genuinely dead session — refresh token from
        // a different project, expired, or revoked).
        let activeSession = session;
        if (session?.access_token) {
          try {
            const { error: userErr } = await supabase.auth.getUser();
            if (userErr && isRecoverableJwtError(userErr.message)) {
              console.warn('[auth] access token rejected, attempting refresh:', userErr.message);
              const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
              if (refreshErr || !refreshed?.session) {
                console.warn('[auth] refresh failed, signing out:', refreshErr?.message);
                await supabase.auth.signOut().catch(() => {});
                try { localStorage.removeItem('sb-amjwnrglafnzwfrctqoh-auth-token'); } catch {}
                handleSession(null);
                return;
              }
              // Refresh succeeded: continue with the new, valid session.
              activeSession = refreshed.session;
            }
          } catch (e) {
            console.warn('[auth] getUser check failed (non-fatal):', e);
          }
        }

        handleSession(activeSession?.user ?? null);
      } catch (err: unknown) {
        clearTimeout(sessionTimeout);
        console.error('getSession failed:', err);
        if (mountedRef.current) {
          if (isLockStealError(err)) {
            // Another tab stole the auth Web Lock mid-bootstrap. Steals are
            // transient (the thief holds the lock for one token round-trip),
            // so retry ONCE silently before showing anything. The retry goes
            // through the full serialized checkSession, so a success sets
            // initializedRef/connectionError exactly like a first-try success.
            if (!lockStealRetriedRef.current) {
              lockStealRetriedRef.current = true;
              setTimeout(() => {
                if (mountedRef.current && !initializedRef.current) void checkSession();
              }, 750);
            } else {
              setMultiTabConflict(true);
              setLoading(false);
            }
          } else {
            setConnectionError(true);
            setLoading(false);
          }
        }
      } finally {
        sessionCheckRef.current = null;
      }
    })();

    sessionCheckRef.current = run;
    return run;
  };

  useEffect(() => {
    mountedRef.current = true;

    // 1. Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip the INITIAL_SESSION event · getSession() below handles initial state.
      if (!initializedRef.current) return;
      // Sync the user state synchronously so consumers (ProtectedRoute,
      // Login's redirect effect) see the new auth state on the same tick the
      // SIGNED_IN/SIGNED_OUT event fires. Without this, navigate("/dash")
      // immediately after sign-in raced the deferred setTimeout below ·
      // ProtectedRoute read user=null/loading=false and bounced the user
      // back to /login, where the redirect effect bounced them to /dash, etc.
      const nextUser = session?.user ?? null;
      // A genuine sign-in has no prior user; a SIGNED_IN re-emitted on
      // tab-refocus / token-refresh does (we are already logged in).
      const hadUser = userIdRef.current !== null;
      const idChanged = (nextUser?.id ?? null) !== userIdRef.current;

      // Same identity, already logged in (TOKEN_REFRESHED / USER_UPDATED, or a
      // SIGNED_IN that Supabase re-emits on every tab refocus): do NOTHING.
      // Calling setUser with a fresh object + re-running handleSession here
      // re-renders every useAuth consumer and triggers the dashboard's realtime +
      // polling refetch burst on EVERY tab return, which reads as the dash
      // "glitching/refreshing". Nothing changed but the JWT, which supabase-js
      // rotates internally regardless. A real sign-in (identity changed / no prior
      // user) or a sign-out still flows through below.
      if (!idChanged && hadUser && event !== "SIGNED_OUT") {
        // A USER_UPDATED for the same identity carries fresh user_metadata —
        // e.g. full_name set from the account-settings save or a completed
        // onboarding wizard. Refresh just the user object so consumers like the
        // sidebar user card reflect the new name immediately, WITHOUT re-running
        // the heavy per-session lookups (company/AAL/role) or the realtime +
        // polling refetch burst that a full handleSession triggers. Every other
        // same-identity event (TOKEN_REFRESHED, tab-refocus SIGNED_IN) stays a
        // no-op as before.
        if (event === "USER_UPDATED" && mountedRef.current) {
          setUser(nextUser);
        }
        return;
      }

      if (mountedRef.current) {
        setUser(nextUser);
        // Gate the whole app behind the loading spinner ONLY for a real auth
        // transition: a fresh sign-in (no prior user) or a sign-out. loading=true
        // makes ProtectedRoute swap the entire dashboard for <PageFallback/>,
        // which UNMOUNTS every page and erases in-progress work. The genuine-login
        // gate is preserved (hadUser is false at login), so the navigate("/dash")
        // redirect-loop fix still holds.
        if (event === "SIGNED_OUT" || (event === "SIGNED_IN" && !hadUser)) {
          setLoading(true);
        }
      }
      userIdRef.current = nextUser?.id ?? null;
      // The heavier per-session work (company/AAL/role lookups) still runs
      // inside setTimeout to avoid the documented Supabase deadlock when
      // calling supabase.auth.* from inside an onAuthStateChange callback.
      setTimeout(() => handleSession(nextUser), 0);
    });

    // 2. Immediately check for existing session (serialized + timeout fallback)
    void checkSession();

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    // Clear local state first so any in-flight queries that resolve mid-signout
    // don't repopulate the cache from the perspective of the previous user.
    if (mountedRef.current) {
      setUser(null);
      setCompanyId(null);
      setAal(null);
    }
    // Drop every cached query so a different user logging in on the same
    // browser tab does not see stale tenant data from the previous session.
    queryClient.clear();
    // Same reasoning for the scanner's localStorage resume markers: they are
    // NOT tenant-scoped, so without this a different user on the same browser
    // would restore the previous user's batch (their uploaded FILENAMES showed
    // as "lost track" cards). Clear both scan-draft markers on sign-out.
    clearBatchInflight();
    clearInflightScan();
    resetPostHog();
    setSentryUser(null);
    setSentryCompany(null);
    await supabase.auth.signOut();
  };

  const retryConnection = () => {
    setConnectionError(false);
    setMultiTabConflict(false);
    // Every manual Try Again earns one fresh silent lock-steal retry; without
    // this a second steal after the first retry would land straight back on
    // the multi-tab screen even once the other tab went quiet.
    lockStealRetriedRef.current = false;
    setLoading(true);
    // Route through the same serialized check. If the initial getSession() is
    // still in flight (waiting on the contended lock), this REUSES it instead
    // of firing a competing call — which is exactly what caused the
    // "Retry getSession failed: … 'steal'" loop. If the prior check already
    // settled, a fresh one starts.
    if (!sessionCheckRef.current) initializedRef.current = false;
    void checkSession();
  };

  return (
    <AuthContext.Provider value={{ user, loading, companyId, aal, connectionError, multiTabConflict, signOut, retryConnection }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
